import {
  Exclude,
  Expose,
  instanceToPlain,
  plainToInstance,
  Transform,
} from 'class-transformer';
import * as t from 'io-ts';
import { validateJSON, validateJSONNow } from '../web/validation';
import { Entity } from './entity';
import { getDatabaseCodeTransformer } from './transfomers/transformers';
import { SystemProcessType, toSystemProcessType } from './system-process-type';
import {
  SystemProcessStatus,
  toSystemProcessStatus,
} from './system-process-status';
import {
  SystemProcessTriggerType,
  toSystemProcessTriggerType,
} from './system-process-trigger-type';
import { ConnectorType, toConnectorType } from './connector-type';
import {
  SystemProcessDomain,
  toSystemProcessDomain,
} from './system-process-domain';

export const SystemProcessExecutionCodec = t.intersection([
  t.type({
    id: t.string,
    name: t.string,
    type: t.string,
    status: t.string,
    processKey: t.string,
    processDomain: t.string,
    triggerType: t.string,
    startDttm: t.number,
  }),
  t.partial({
    parentId: t.string,
    systemKey: t.string,
    systemAccountKey: t.string,
    connectorType: t.string,
    connectorRef: t.string,
    endDttm: t.number,
    heartbeatDttm: t.number,
    successCount: t.number,
    warningCount: t.number,
    failureCount: t.number,
    summaryMessage: t.string,
    context: t.string,
    metadata: t.string,
    createDttm: t.number,
    lastModifiedDttm: t.number,
    artifactCount: t.number,
    issueCount: t.number,
    metricCount: t.number,
    missingMappingCount: t.number,
    downloadUrl: t.string,
  }),
]);

export type SystemProcessExecution = t.TypeOf<
  typeof SystemProcessExecutionCodec
>;

@Exclude()
export class SystemProcessExecutionEntity implements Entity {
  @Expose() id!: string;
  @Expose() name!: string;
  @Expose()
  @Transform(getDatabaseCodeTransformer(toSystemProcessType))
  type!: SystemProcessType;
  @Expose()
  @Transform(getDatabaseCodeTransformer(toSystemProcessStatus))
  status!: SystemProcessStatus;
  @Expose() processKey!: string;
  @Expose()
  @Transform(getDatabaseCodeTransformer(toSystemProcessDomain))
  processDomain!: SystemProcessDomain;
  @Expose()
  @Transform(getDatabaseCodeTransformer(toSystemProcessTriggerType))
  triggerType!: SystemProcessTriggerType;
  @Expose() startDttm!: number;

  @Expose() parentId?: string;
  @Expose() systemKey?: string;
  @Expose() systemAccountKey?: string;
  @Expose()
  @Transform(getDatabaseCodeTransformer(toConnectorType))
  connectorType?: ConnectorType;
  @Expose() connectorRef?: string;
  @Expose() endDttm?: number;
  @Expose() heartbeatDttm?: number;
  @Expose() successCount?: number;
  @Expose() warningCount?: number;
  @Expose() failureCount?: number;
  @Expose() summaryMessage?: string;
  @Expose() context?: string;
  @Expose() metadata?: string;

  @Expose() createDttm?: number;
  @Expose() lastModifiedDttm?: number;
  @Expose() artifactCount?: number;
  @Expose() issueCount?: number;
  @Expose() metricCount?: number;
  @Expose() missingMappingCount?: number;
  @Expose() downloadUrl?: string;

  readonly entityType = 'SYSPROCEXEC';

  get displayName(): Readonly<string> {
    return this.id;
  }

  static from(value: SystemProcessExecution): SystemProcessExecutionEntity {
    return plainToInstance(SystemProcessExecutionEntity, value, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<SystemProcessExecution> {
    return validateJSON(
      SystemProcessExecutionCodec,
      instanceToPlain(this, { excludeExtraneousValues: true })
    );
  }

  asTypeValue(): SystemProcessExecution {
    return validateJSONNow(
      SystemProcessExecutionCodec,
      instanceToPlain(this, { excludeExtraneousValues: true })
    );
  }
}

export interface SystemProcessExecutionSearchCriteria {
  fromDttm?: number;
  toDttm?: number;
  type?: string;
  status?: string;
  domain?: string;
  q?: string;
}

export interface SystemProcessExecutionPageSearchCriteria
  extends SystemProcessExecutionSearchCriteria {
  page: number;
  size: number;
}
